import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { useAppDispatch } from '../../../hooks/use-app-dispatch';
import analytics from '../../../lib/analytics';
import { checkOrderInProgress } from '../../../lib/side-effects/provisioning-statuses';
import { selectIsNonAdmin, selectState } from '../../../selectors';

// Shared by pricing-tiers.tsx and hero.tsx. 'contact' ctas open the same ChiliPiper "Contact
// Sales" scheduling dialog used on the Product Catalog list page (see onContactClick, wired up in
// containers/product-catalog/product-detail.tsx). 'buy' ctas with an offerFlow set navigate
// there, landing on that offer's bare URL the same way every other offer entry point works;
// single-plan offers with Pages.OrderReview then auto-advance straight to their Order Review page
// (see OfferPlanSelect's single-plan auto-advance + OfferContainer's routing) -- unless another
// product order is still being provisioned (BRGUI-2696), in which case onOrderInProgress fires
// instead and navigation is skipped. 'trial' ctas are tracked but not yet wired to a real flow.
type CtaLike = {
  ctaAction: 'trial' | 'buy' | 'contact';
  offerFlow?: string;
};

export const trackCtaClick = (id: string) => analytics.track(`Product detail pricing CTA clicked: ${id}`);

// onOrderInProgress is only ever consulted for 'buy' ctas -- 'contact' ctas aren't purchasing
// anything that could itself be mid-provisioning, so checkOrderInProgress is never called for them.
export const usePricingCtaClick = (onContactClick: () => void, onOrderInProgress: () => void) => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useAppDispatch();

  return async (id: string, cta: CtaLike) => {
    trackCtaClick(id);

    if (cta.ctaAction === 'buy' && cta.offerFlow) {
      if (await checkOrderInProgress(dispatch)) {
        onOrderInProgress();
        return;
      }
      // Forwards this page's own query string (e.g. ?shouldShowProductCatalog=true, a non-prod
      // override for the catalog's gating flag) the same way hero.tsx's own primary-cta <Link>
      // and catalog-card.tsx do for their navigation — without it, Order Review's breadcrumb has
      // nothing left to forward back to a renderable PDP (BRGUI-2570).
      history.push(`/${cta.offerFlow}${location.search}`);
    }
    if (cta.ctaAction === 'contact') onContactClick();
  };
};

// Shared by pricing-tiers.tsx and hero.tsx — only a non-admin who is actually logged in is
// blocked from completing a purchase; a logged-out visitor still needs to be able to click
// through to sign up/log in as part of the buy flow.
export const useIsBlockedFromPurchasing = () => {
  const { isLoggedIn } = useSelector(selectState)?.account ?? {};
  const isNonAdmin = useSelector(selectIsNonAdmin);
  return Boolean(isLoggedIn) && isNonAdmin;
};
